<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, ChevronDown, ChevronUp, CircleAlert, CloudUpload, Minus } from '@lucide/vue'
import { Button, InlineAlert, Progress } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useBackupQueueStore } from '../../stores/backup-queue'
import { useLibraryStore } from '../../stores/library'
import { useUploadStore } from '../../stores/upload'
import type { BackupQueueStatus } from '../../lib/backup-queue-entry'
import { showsBackupBar } from '../../lib/backup-queue'
import { useControlSize } from '../../lib/library-layout'

/**
 * 備份狀態列。固定在畫面底部，本地範圍一律呈現——只在有事情做時才出現的話，使用者
 * 既找不到入口也看不出狀態。不是對話框：關掉就看不到進度與失敗原因正是要避免的。
 *
 * 收合狀態的高度由 library-content 的內距讓出，因此不會遮住清單最後一列；清單往上
 * 展開時才覆蓋，而那是使用者當下的明確動作。系統性暫停用 inline alert 不用 toast，
 * toast 會被下一則覆蓋。
 */
const emit = defineEmits<{ inspect: [comic: Comic] }>()
const queue = useBackupQueueStore()
const library = useLibraryStore()
const upload = useUploadStore()
const controlSize = useControlSize()
const expanded = ref(false)

const visible = computed(() => showsBackupBar(library.supported, library.hasSources))
const currentPercent = computed(() => upload.totalParts
  ? Math.round(upload.completedParts / upload.totalParts * 100)
  : 0)
const statusLabels: Record<BackupQueueStatus, string> = {
  queued: '等待中', running: '正在備份', done: '已完成', failed: '失敗', skipped: '已跳過',
}
const titleOf = (comicId: string) => library.comics.find(comic => comic.id === comicId)?.title ?? comicId

/**
 * 明細分組。失敗排最前面——那是唯一需要使用者做事的一群。
 *
 * 已完成只留這一輪的：備份好的作品在下一次排程時就不再留佇列項，因此這一段會自然清空，
 * 明細不會隨藏書一起無限長。
 */
const groups = computed(() => [
  { key: 'failed', label: '失敗', entries: queue.ordered.filter(entry => entry.status === 'failed') },
  { key: 'pending', label: '待備份', entries: queue.ordered.filter(entry => entry.status === 'queued' || entry.status === 'running') },
  { key: 'skipped', label: '已跳過', entries: queue.ordered.filter(entry => entry.status === 'skipped') },
  { key: 'done', label: '這一輪已完成', entries: queue.ordered.filter(entry => entry.status === 'done') },
].filter(group => group.entries.length))

const summary = computed(() => {
  const parts: string[] = []
  // 跑完之後以那一輪的結果為準：已完成的佇列項會被收掉，數不到。
  if (queue.finished && queue.lastRun) {
    const run = queue.lastRun
    if (run.done) parts.push(`已完成 ${run.done} 本`)
    if (run.failed) parts.push(`失敗 ${run.failed} 本`)
    if (run.skipped) parts.push(`跳過 ${run.skipped} 本`)
    if (queue.pendingCount) parts.push(`還有 ${queue.pendingCount} 本待備份`)
    return parts.join(' · ') || '這一輪沒有要備份的作品'
  }
  if (queue.pendingCount) parts.push(`${queue.pendingCount} 本尚未備份`)
  if (queue.failedEntries.length) parts.push(`失敗 ${queue.failedEntries.length} 本`)
  if (queue.skippedCount) parts.push(`跳過 ${queue.skippedCount} 本`)
  return parts.join(' · ') || '全部作品都已備份'
})

function inspect(comicId: string) {
  const comic = library.comics.find(item => item.id === comicId)
  if (comic) emit('inspect', comic)
}
</script>

<template>
  <section v-if="visible" class="backup-bar" aria-label="雲端備份">
    <!-- 清單在列之上，展開時往上長。 -->
    <div v-if="expanded && groups.length" class="backup-bar-list">
      <section v-for="group in groups" :key="group.key">
        <h3 class="backup-bar-group">{{ group.label }}<span>{{ group.entries.length }}</span></h3>
        <ul>
          <li v-for="entry in group.entries" :key="entry.comicId" :class="`is-${entry.status}`">
            <Check v-if="entry.status === 'done'" :size="15" aria-hidden="true" />
            <CircleAlert v-else-if="entry.status === 'failed'" :size="15" aria-hidden="true" />
            <Minus v-else-if="entry.status === 'skipped'" :size="15" aria-hidden="true" />
            <CloudUpload v-else :size="15" aria-hidden="true" />
            <span class="backup-bar-title">{{ titleOf(entry.comicId) }}</span>
            <small>{{ statusLabels[entry.status] }}{{ entry.reason ? ` · ${entry.reason}` : '' }}</small>
            <Button
              v-if="entry.status === 'failed' && !queue.running"
              plain
              :size="controlSize"
              @click="inspect(entry.comicId)"
            >處理</Button>
          </li>
        </ul>
      </section>
    </div>

    <InlineAlert v-if="queue.paused" tone="warning" class="backup-bar-alert">
      <span>備份已暫停：{{ queue.pausedReason }}其餘作品仍在等待中。</span>
      <Button plain :size="controlSize" class="ml-2" @click="queue.retry()">重試</Button>
    </InlineAlert>

    <div class="backup-bar-row">
      <div class="backup-bar-status">
        <CloudUpload :size="17" aria-hidden="true" />
        <p role="status" aria-live="polite">
          <template v-if="queue.running">
            第 {{ queue.position }} ／ 共 {{ queue.total }} 本<span v-if="queue.activeComic"> · {{ queue.activeComic.title }}</span>
          </template>
          <template v-else>{{ summary }}</template>
        </p>
      </div>

      <div v-if="queue.running" class="backup-bar-progress">
        <Progress size="sm" color="primary" :value="currentPercent" :aria-label="`目前作品的備份進度 ${currentPercent}%`" />
        <small v-if="upload.progressLabel">{{ upload.progressLabel }}</small>
      </div>

      <div class="backup-bar-actions">
        <Button
          v-if="queue.running"
          outline
          :size="controlSize"
          :disabled="queue.stopRequested"
          @click="queue.requestStop()"
        >{{ queue.stopRequested ? '這一本結束後停止…' : '停止' }}</Button>
        <Button
          v-else-if="queue.pendingCount"
          color="primary"
          :size="controlSize"
          :disabled="!library.ready"
          @click="queue.start()"
        >開始備份（{{ queue.pendingCount }} 本）</Button>
        <Button
          v-if="!queue.running && queue.failedEntries.length"
          plain
          :size="controlSize"
          @click="queue.retry()"
        >重試失敗的 {{ queue.failedEntries.length }} 本</Button>
        <Button
          v-if="groups.length"
          plain
          :size="controlSize"
          :aria-expanded="expanded"
          @click="expanded = !expanded"
        >
          <component :is="expanded ? ChevronDown : ChevronUp" :size="16" aria-hidden="true" />{{ expanded ? '收合' : '明細' }}
        </Button>
      </div>
    </div>
  </section>
</template>
